import React, { useState } from "react";
import styled, { keyframes } from "styled-components";

const CalendarWrapper = styled.div`
  width: 200px;
  height: 200px;
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column;
  font-family: sans-serif;
  padding: 5px;
  border-radius: 10px;
  box-shadow: 4px 4px 4px #f0f0f0;
`;

const MonthHeader = styled.div`
  text-align: center;
  font-weight: bold;
  padding: 4px;
  font-size: 0.9rem;
`;

const DaysGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  font-size: 0.7rem;
`;

const DayCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 1px;
  padding: 2px 0;
  box-sizing: border-box;
  border-radius: 3px;
  ${({ eventType }) =>
    eventType === "game" &&
    `
    background-color: #add8e6;
    cursor: pointer;
    transition: 0.3s ease-in-out;
    &:hover {
        background-color: #9cc7d5;
    }
  `}
  ${({ eventType }) =>
    eventType === "training" &&
    `
    background-color: #90ee90;
    cursor: pointer;
    transition: 0.3s ease-in-out;
    &:hover {
        background-color: #80dd80;
    }
  `}
`;

// Animacja modala
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  min-width: 250px;
  animation: ${fadeInUp} 0.3s ease forwards;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const CloseButton = styled.button`
  background: #ccc;
  border: none;
  padding: 5px 10px;
  margin-top: 10px;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: #bbb;
  }
`;

const dayMap = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

function expandEventsForMonth(events, monthIndex, year) {
  const results = [];

  events.forEach((ev) => {
    if (!ev.rrule) {
      if (ev.dtstart.getFullYear() === year && ev.dtstart.getMonth() === monthIndex) {
        results.push({ date: ev.dtstart.getDate(), summary: ev.summary });
      }
      return;
    }

    const rules = Object.fromEntries(
      ev.rrule.split(";").map((part) => {
        const [k, v] = part.split("=");
        return [k, v];
      })
    );

    if (rules.FREQ === "WEEKLY" && rules.BYDAY) {
      const days = rules.BYDAY.split(",");
      const interval = parseInt(rules.INTERVAL || "1", 10);
      days.forEach((day) => {
        const dayIndex = dayMap[day];
        for (let d = 1; d <= 7; d++) {
          const date = new Date(year, monthIndex, d);
          if (date.getDay() === dayIndex) {
            for (
              let dateNum = d;
              dateNum <= new Date(year, monthIndex + 1, 0).getDate();
              dateNum += 7 * interval
            ) {
              results.push({ date: dateNum, summary: ev.summary });
            }
            break;
          }
        }
      });
    }

    if (rules.FREQ === "MONTHLY" && rules.BYDAY) {
      const match = rules.BYDAY.match(/^(\d)([A-Z]{2})$/);
      if (match) {
        const nth = parseInt(match[1], 10);
        const dayIndex = dayMap[match[2]];
        let count = 0;
        for (let d = 1; d <= 31; d++) {
          const date = new Date(year, monthIndex, d);
          if (date.getDay() === dayIndex) {
            count++;
            if (count === nth) {
              results.push({ date: d, summary: ev.summary });
              break;
            }
          }
        }
      }
    }
  });

  return results;
}

const CalendarMonth = ({ m, events = [] }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);

  const monthIndex = m - 1;
  const year = new Date().getFullYear();
  const monthName = new Date(year, monthIndex).toLocaleString("pl-PL", { month: "long" });

  const firstDay = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, m, 0).getDate();

  const eventsMap = {};
  const expandedEvents = expandEventsForMonth(events, monthIndex, year);
  expandedEvents.forEach((e) => {
    eventsMap[e.date] = e.summary;
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push(<DayCell key={`empty-${i}`} />);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const eventType = eventsMap[d];
    cells.push(
      <DayCell
        key={d}
        eventType={eventType}
        onClick={() => eventType && setSelectedEvent({ day: d, type: eventType })}
      >
        {d}
      </DayCell>
    );
  }

  return (
    <>
      <CalendarWrapper>
        <MonthHeader>{monthName}</MonthHeader>
        <DaysGrid>
          {["Nd", "Pn", "Wt", "Śr", "Cz", "Pt", "Sb"].map((day, i) => (
            <DayCell key={`dow-${i}`}>
              <strong>{day}</strong>
            </DayCell>
          ))}
          {cells}
        </DaysGrid>
      </CalendarWrapper>

      {selectedEvent && (
        <ModalOverlay onClick={() => setSelectedEvent(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Wydarzenie</h3>
            <p>
              <strong>Dzień:</strong> {selectedEvent.day}
            </p>
            <p>
              <strong>Typ:</strong> {selectedEvent.type}
            </p>
            <CloseButton onClick={() => setSelectedEvent(null)}>Zamknij</CloseButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default CalendarMonth;

// Przykład użycia:
const icsData = [
  { summary: "training", dtstart: new Date(2025, 7, 5), rrule: "FREQ=WEEKLY;BYDAY=TU" },
  { summary: "training", dtstart: new Date(2025, 7, 6), rrule: "FREQ=WEEKLY;BYDAY=WE" },
  { summary: "training", dtstart: new Date(2025, 7, 1), rrule: "FREQ=MONTHLY;BYDAY=1FR" },
  { summary: "game", dtstart: new Date(2025, 7, 2), rrule: "FREQ=WEEKLY;INTERVAL=1;BYDAY=SA" },
];

export const EventsCalendar = ({ dates = icsData }) => {
  return <CalendarMonth m={11} events={dates} />;
};
